<template>
    <section class="l2-faq" id="faq">
        <div class="l2-faq__container">
            <div class="l2-faq__header">
                <h2 class="l2-faq__title">
                    Questions?<br />
                    <span class="l2-faq__title-accent">We've got answers.</span>
                </h2>
            </div>

            <div class="l2-faq__list">
                <div
                    v-for="(faq, index) in faqs"
                    :key="index"
                    class="l2-faq-item"
                    :class="{ 'l2-faq-item--open': openIndex === index }"
                    @click="toggle(index)">
                    <div class="l2-faq-item__header">
                        <h3 class="l2-faq-item__question">{{ faq.q }}</h3>
                        <div class="l2-faq-item__icon">
                            <v-icon size="20">{{ openIndex === index ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
                        </div>
                    </div>
                    <Transition name="l2-faq-expand">
                        <div v-if="openIndex === index" class="l2-faq-item__answer">
                            <p v-for="(paragraph, paragraphIndex) in answerParagraphs(faq.a)" :key="paragraphIndex">
                                {{ paragraph }}
                            </p>
                        </div>
                    </Transition>
                </div>
            </div>

            <div class="l2-faq__contact">
                <span class="l2-faq__contact-text">Have another question, or comment?</span>
                <v-btn variant="text" class="l2-faq__contact-button" @click="$emit('contact')">Contact us</v-btn>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { ref } from 'vue';

    interface Faq {
        q: string;
        a: string;
    }

    defineProps<{
        faqs: Faq[];
    }>();

    defineEmits<{
        (e: 'contact'): void;
    }>();

    const openIndex = ref<number | null>(0);

    const answerParagraphs = (answer: string) =>
        answer
            .split(/\n\s*\n/)
            .map((paragraph) => paragraph.trim())
            .filter(Boolean);

    const toggle = (index: number) => {
        openIndex.value = openIndex.value === index ? null : index;
    };
</script>

<style lang="scss" scoped>
    .l2-faq {
        position: relative;
        padding: var(--l2-section-padding-y) 24px;
        background: var(--l2-bg);

        &__container {
            max-width: 700px;
            margin: 0 auto;
        }

        &__header {
            text-align: center;
            margin-bottom: 48px;
        }

        &__title {
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 700;
            line-height: 1.2;
            color: var(--l2-text);
        }

        &__title-accent {
            color: var(--landing-muted);
        }

        &__list {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        &__contact {
            margin-top: 28px;
            text-align: center;
        }

        &__contact-text {
            display: block;
            color: var(--l2-text-muted);
            margin-bottom: 4px;
        }

        &__contact-button {
            color: var(--l2-accent);
            text-transform: none;
            letter-spacing: normal;
        }
    }

    .l2-faq-item {
        background: var(--l2-bg-elevated);
        border: 1px solid var(--l2-border-subtle);
        border-radius: var(--l2-radius-sm);
        overflow: hidden;
        cursor: pointer;
        transition: all var(--l2-transition);

        &:hover {
            border-color: var(--l2-border);
        }

        &--open {
            border-color: var(--l2-border);
            background: var(--l2-surface);
        }

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 20px 24px;
        }

        &__question {
            font-size: 1rem;
            font-weight: 600;
            color: var(--l2-text);
            margin: 0;
        }

        &__icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: var(--l2-accent-soft);
            border-radius: 8px;
            transition: all var(--l2-transition);

            .v-icon {
                color: var(--l2-accent);
            }
        }

        &--open &__icon {
            background: var(--l2-accent);

            .v-icon {
                color: var(--l2-bg) !important;
            }
        }

        &__answer {
            padding: 0 24px 20px;

            p {
                font-size: 0.9375rem;
                line-height: 1.7;
                color: var(--l2-text-muted);
                margin: 0;

                & + p {
                    margin-top: 12px;
                }
            }
        }
    }

    .l2-faq-expand-enter-active,
    .l2-faq-expand-leave-active {
        transition: all 0.3s ease;
        overflow: hidden;
    }

    .l2-faq-expand-enter-from,
    .l2-faq-expand-leave-to {
        opacity: 0;
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
    }

    .l2-faq-expand-enter-to,
    .l2-faq-expand-leave-from {
        opacity: 1;
        max-height: 200px;
    }

    @media (max-width: 600px) {
        .l2-faq {
            &__header {
                margin-bottom: 32px;
            }
        }
    }
</style>
